import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { useGetSheetNames } from "../../hooks";
import { earliestDateInBlob, SHIFTS_DATE_COLUMN } from "../../loadOrder";
import { ACCEPTED_TYPES } from "../../utils";
import { type DataLoadFile, type DataLoadFileKind, type DataLoadMode, isExcelFile, newDataLoadFile, type RosterAction } from "./consts";
import { buildLoadOrderEntries, buildOrderedFiles } from "./dataLoadOrder";
import { type PromptSeeds, promptAfterKindChange, seedFor } from "./dataLoadPrompts";

const isCsv = (file: File) => file.name.toLowerCase().endsWith(".csv");

/** A direct-mode shifts file is never transformed, so its sort key can only come from the raw file. */
const canReadDateDirectly = (file: DataLoadFile) => file.kind === "shifts" && file.mode === "direct" && isCsv(file.file);

type Params = {
	seedsRef: { current: PromptSeeds };
};

export function useDataLoadFiles({ seedsRef }: Params) {
	const [files, setFiles] = useState<DataLoadFile[]>([]);
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { mutateAsync: fetchSheetNames } = useGetSheetNames();

	const updateFile = (fileId: string, partial: Partial<DataLoadFile>) => {
		setFiles((prev) => prev.map((entry) => (entry.id === fileId ? { ...entry, ...partial } : entry)));
	};

	/**
	 * Recompute the sort key for a direct-mode shifts CSV. Transform-mode files get theirs from the
	 * transformed CSV instead; a direct .xlsx cannot be parsed client-side and stays undated, which
	 * the load-order caption says out loud.
	 */
	const refreshDirectShiftsDate = (entry: DataLoadFile) => {
		if (!canReadDateDirectly(entry)) return;
		void earliestDateInBlob(entry.file, SHIFTS_DATE_COLUMN).then((date) => updateFile(entry.id, { earliestDate: date }));
	};

	/**
	 * The drop zone already filters by extension; it just used to do it silently, so a dragged file
	 * that never appeared looked like a bug in the page rather than a rejected file.
	 */
	const reportRejected = (rejected: File[]) => {
		showSnackbar(
			t("onboarding_load.unsupported_files", {
				count: rejected.length,
				files: rejected.map((file) => file.name).join(", "),
				types: ACCEPTED_TYPES.join(", "),
			}),
			"warning",
		);
	};

	const addFiles = (picked: File[]) => {
		if (picked.length === 0) return;
		const added = picked.map(newDataLoadFile);
		setFiles((prev) => [...prev, ...added]);

		// Read-only and independent per file, so in parallel — the old Transform tab did these serially,
		// which meant ten Excel files were ten round trips before the onboarder could type anything.
		for (const entry of added.filter((e) => isExcelFile(e.file))) {
			void fetchSheetNames(entry.file)
				.then((names) => updateFile(entry.id, { sheetNames: names }))
				.catch(() => {
					// Surfaced by the hook's onError snackbar; the file still loads without sheet instructions.
				});
		}
	};

	const removeFile = (fileId: string) => setFiles((prev) => prev.filter((entry) => entry.id !== fileId));

	const setKind = (fileId: string, kind: DataLoadFileKind) => {
		setFiles((prev) =>
			prev.map((entry) => {
				if (entry.id !== fileId) return entry;
				// The transform output is shaped by the kind it ran as — a roster CSV rendered as shifts
				// reaches the wrong loader with none of the fields it reads. Drop it so the file is simply
				// un-transformed again.
				return {
					...entry,
					kind,
					prompt: promptAfterKindChange(entry, kind, seedsRef.current),
					tabInstructions: entry.promptDirty ? entry.tabInstructions : seedFor(kind, seedsRef.current).tabInstructions,
					transformedData: null,
					failedTabs: [],
					filteredBlob: null,
					earliestDate: null,
					transformError: null,
				};
			}),
		);
		const entry = files.find((f) => f.id === fileId);
		if (entry) refreshDirectShiftsDate({ ...entry, kind });
	};

	const setMode = (fileId: string, mode: DataLoadMode) => {
		updateFile(fileId, { mode, transformedData: null, failedTabs: [], filteredBlob: null, earliestDate: null, transformError: null });
		const entry = files.find((f) => f.id === fileId);
		if (entry) refreshDirectShiftsDate({ ...entry, mode });
	};

	const setRosterAction = (fileId: string, rosterAction: RosterAction) => updateFile(fileId, { rosterAction });

	// Marks the file dirty, which permanently stops every re-seed path from touching it.
	const setPrompt = (fileId: string, prompt: string) => updateFile(fileId, { prompt, promptDirty: true });

	const setTabInstruction = (fileId: string, sheet: string, value: string) => {
		setFiles((prev) => prev.map((entry) => (entry.id === fileId ? { ...entry, tabInstructions: { ...entry.tabInstructions, [sheet]: value } } : entry)));
	};

	const setSkip = (fileId: string, skip: boolean) => updateFile(fileId, { skip });

	const resetFiles = () => setFiles([]);

	const orderedFiles = buildOrderedFiles(files);
	const loadOrderEntries = buildLoadOrderEntries(orderedFiles);
	const unsetKindCount = files.filter((entry) => entry.kind === null).length;
	const hasTemplate = files.some((entry) => entry.kind === "template");
	const hasShiftsOrTemplate = files.some((entry) => entry.kind === "shifts" || entry.kind === "template");
	// A direct .xlsx has no readable date, so it sorts after every dated shifts file regardless of when
	// it actually starts. The caption warns rather than guessing.
	const hasUndatedDirectShifts = files.some((entry) => entry.kind === "shifts" && entry.mode === "direct" && !isCsv(entry.file));

	return {
		files,
		setFiles,
		updateFile,
		addFiles,
		reportRejected,
		removeFile,
		setKind,
		setMode,
		setRosterAction,
		setPrompt,
		setTabInstruction,
		setSkip,
		resetFiles,
		orderedFiles,
		loadOrderEntries,
		unsetKindCount,
		hasTemplate,
		hasShiftsOrTemplate,
		hasUndatedDirectShifts,
	};
}
